import {
  Backdrop,
  ClickAwayListener,
  Popper,
  Stack,
  styled,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import { useFlag } from '@schooly/hooks/use-flag';
import { FC, ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { SimpleButton } from './SimpleButton/SimpleButton';

export type ContextMenuAction = {
  onClick: (close?: () => void, e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  color?: string;
  icon?: ReactNode;
} & ({ labelTextId: string; label?: string } | { label: string; labelTextId?: string });

export type ContextMenuProps = {
  children: (open: () => void, opened: boolean) => ReactElement<any, any>;
  actions?: ContextMenuAction[];
  renderMenuContent?: (handleClose: () => void) => ReactNode;
} & Partial<Omit<TooltipProps, 'children'>>;

const noop = () => {};

export const ContextMenu: FC<ContextMenuProps> = ({
  children,
  actions = [],
  renderMenuContent,
  ...tooltipProps
}) => {
  const [opened, open, close] = useFlag();
  const { $t } = useIntl();

  const renderContent = () => {
    if (renderMenuContent) {
      return renderMenuContent(close);
    }

    return actions.map((a) => {
      const label = a.label ?? $t({ id: a.labelTextId! });

      return (
        <SimpleButton
          onClick={(e) => a.onClick(close, e)}
          key={label}
          startIcon={a.icon}
          sx={{ color: a.color }}
        >
          {label}
        </SimpleButton>
      );
    });
  };

  return (
    <>
      <Backdrop open={opened} invisible sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} />
      <ClickAwayListener onClickAway={opened ? close : noop}>
        <div>
          <Tooltip
            PopperProps={{ disablePortal: true }}
            onClose={close}
            open={opened}
            placement="bottom-start"
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <Stack sx={{ gap: 0.5, alignItems: 'start', flexWrap: 'wrap' }} className="container">
                {renderContent()}
              </Stack>
            }
            {...tooltipProps}
          >
            {children(open, opened)}
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};

export const MenuItemArrow = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
  width: theme.spacing(),

  '& > .MuiPaper-root': {
    position: 'absolute',
    width: theme.spacing(2),
    height: theme.spacing(2),
    background: theme.palette.background.paper,
    borderRadius: 2, // non-standard shape
    transform: 'rotate(-45deg)',
    transformOrigin: 'right bottom',
    boxShadow: 'none',
  },
}));
